/**
 * **« Pas ouvert chez vous » n'est pas « pas le droit »** (#2228).
 *
 * Depuis `suivora-api#1716`, une route de rubrique fermée répond `403` avec
 * `code: 'FEATURE_NOT_ENABLED'` et la rubrique nommée. Le code arrivait jusqu'au client sans que
 * rien ne le lise : l'écran affichait son erreur générique, exactement comme pour un droit
 * manquant — et la personne partait réclamer une permission qui ne changera rien.
 *
 * ⚠️ **Ce n'est pas une source de vérité pour le menu.** Les drapeaux se lisent dans
 * `/me/branding` ; déduire une rubrique fermée d'un 403 reçu en cours de route ferait dépendre
 * l'affichage de l'ordre des appels, et un écran qui n'appelle rien resterait visible.
 *
 * ⚠️ **Aucune redirection ici.** Sortir quelqu'un d'un écran depuis un gestionnaire d'erreur,
 * c'est reprendre la navigation à `FeatureSectionGate` qui la porte déjà — et deux mécanismes qui
 * redirigent finissent par se battre. Le refus se dit là où il arrive.
 */

import type { HttpError } from '@/shared/libs/httpError';

export const FEATURE_NOT_ENABLED = 'FEATURE_NOT_ENABLED';

/**
 * Les rubriques nommées en clair.
 *
 * ⚠️ Le catalogue vit dans le code de l'API : celle-ci ne sert **qu'à rendre le message lisible**,
 * et une clé qu'elle ignore n'est pas une erreur — le refus retombe alors sur la phrase du serveur,
 * qui est juste sans nommer la rubrique. Recopier le catalogue entier ici, ce serait promettre de
 * le tenir à jour.
 */
const FEATURE_LABELS: Record<string, string> = {
  'communication-suite': 'Communication',
  'support-desk': 'Support',
  'referral-program': 'Cooptation & apport',
  'annual-report': 'Rapport annuel',
  'dividends-regime': 'Régime dividendes',
  'tenant-accent-color': 'Couleur d’accent',
};

export type FeatureRefusal = {
  /** La clé de la rubrique, telle que le serveur la nomme. */
  feature: string | null;
  /** Ce qu'on affiche : la rubrique nommée quand on la connaît, la phrase du serveur sinon. */
  message: string;
};

/** Ce refus est-il celui d'une rubrique fermée ? `null` pour tout le reste. */
export function featureRefusal(error: unknown): FeatureRefusal | null {
  const http = error as HttpError | null | undefined;
  if (!http || http.code !== FEATURE_NOT_ENABLED) return null;

  const feature = typeof http.feature === 'string' ? http.feature : null;
  const label = feature ? FEATURE_LABELS[feature] : undefined;

  return {
    feature,
    message: label
      ? `La rubrique « ${label} » n’est pas ouverte pour votre organisation. Ce n’est pas un droit manquant : demandez son ouverture à l’éditeur.`
      : /* La phrase du serveur dit déjà la bonne chose ; elle ne nomme simplement pas la
           rubrique. Mieux vaut la reprendre que d'inventer un nom pour une clé inconnue. */
        http.message,
  };
}
